import { create } from "zustand";
import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";
import { deleteBlob, loadBlob, saveBlob } from "@/lib/blob-store";
import { MIME_EXT, extFromFilename, mimeFromExt } from "@/lib/mime-ext";
import { DocsApiError, isDocsAuthReady } from "@/lib/docs-api";
import { GithubStorageError } from "@/lib/github-storage";
import {
  deleteWorkshopBlob,
  downloadWorkshopBlob,
  isWorkshopBlobSyncEnabled,
  listWorkshopBlobs,
  uploadWorkshopBlob,
} from "@/lib/workshop-blob-sync";
import {
  captureIndexMarkdown,
  deleteSearchIndex,
  upsertSearchIndex,
} from "@/lib/search-index-sidecar";
import { broadcastSync } from "@/lib/sync-broadcast";
import { isWorkshopBffEnabled } from "@/env";
import type { Workshop } from "@/content/workshops";

/**
 * Captures store — per-workshop list of user-added capture cards
 * (HAH/STR snapshot PNGs, XAES Analyst chat exports). Mirrors the
 * shape + API of `useAssetLocker` so the two stores stay in lockstep;
 * the split exists because the legacy "Deliverables" surface in the
 * Asset Locker is a curated 6-card list, whereas Captures is the
 * grid that absorbs ad-hoc user-saved artefacts alongside the
 * decorative seed cards in `captures-content.ts`.
 *
 * Backed by `idb-keyval` under `xaes-captures`. No seed assets — the
 * decorative seed cards live in `captures-content.ts` and are merged
 * at the consumer (`CapturesModal`) so this store stays as a pure
 * user-content sink.
 *
 * Phase 6 — when a blob backend is enabled the backend is the source of
 * truth; IDB is the local cache (fast hydrate + offline). The backend is
 * chosen in `workshop-blob-sync.ts`: the production BFF / Azure backend, or
 * the legacy GitHub fallback. Captures mirror the AssetLocker `sync()`
 * reconciliation pattern but write into a SIBLING namespace:
 * `<wsId>/captures/<id>.<ext>` (vs the AssetLocker's `…/uploads/…`). Keeping
 * the surfaces split lets a future audit / share flow address them
 * independently and the backend's blob tree stays visually distinct. The
 * `sync()` action does a pull-then-push reconciliation per workshop; `add()` /
 * `remove()` mirror writes through the adapter fire-and-forget so the local UI
 * stays responsive even on slow networks. When no backend is configured we
 * fall back to the original IDB-only behaviour — no regression for the offline
 * dev path. See `docs/adr/0010-github-persistence.md` for the legacy rationale.
 *
 * Adapted from `useAssetLocker.ts` (same template); see that file's
 * jsdoc for the broader rationale + the `useFindings` sibling.
 */

export type CaptureAccent =
  | "true-blue"
  | "sky"
  | "gold"
  | "green"
  | "melon"
  | "red";

export type CaptureKind = "whiteboard" | "screen" | "snapshot" | "chat";

export type UserCapture = {
  /** uuid-ish, unique within the workshop scope */
  id: string;
  /** Workshop scope. Stored on the entry too so the value survives
   *  shape-shifts without re-deriving from the bucket key. */
  workshopId: string;
  title: string;
  /** Activity / session label, e.g. "Activity 2 — HAH",
   *  "XAES Analyst chat". */
  activity: string;
  /** Formatted "HH:MM" of `addedAt`. Pre-formatted at write time so
   *  the grid card doesn't re-derive on every render. */
  timestamp: string;
  kind: CaptureKind;
  accent: CaptureAccent;
  source: "Saved" | "Auto-captured";
  /** One-line caption shown in the lightbox under the thumbnail. */
  caption: string;
  /**
   * Pointer into `src/lib/blob-store.ts`. Present for captures that
   * carry binary payloads (snapshot PNGs). The blob is fetched on
   * render — never kept in zustand memory.
   */
  blobKey?: string;
  /** MIME type of `blobKey` payload — e.g. "image/png",
   *  "text/markdown". */
  mimeType?: string;
  /** Data-URL fallback for chat-export markdown — kept alongside any
   *  future blob-backed mirror so the existing share path still works. */
  url?: string;
  addedAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 reserved seam). Optional; absent on legacy
   *  entries, defaulted to `[]` at every read site. */
  tags?: string[];
  /**
   * Phase 6 — GitHub Contents API path, e.g.
   * `"workshops/WS4/captures/abc.png"`. Set once the capture has synced.
   * Used as the delete key + the durability signal (absent → IDB-only).
   */
  githubPath?: string;
  /** Phase 6 — GitHub blob SHA. Required for delete / update. */
  githubSha?: string;
  /**
   * Phase 6 — ISO timestamp from the last GitHub commit touching this
   * capture. Drives the "last synced" UI hint.
   */
  githubSyncedAt?: string;
};

type EntriesByWorkshop = Record<Workshop["id"], UserCapture[]>;

/**
 * Phase 6 — adapter-mirror sync state surfaced on the store so the
 * workshop header can render a status pill ("synced" / "syncing" / …).
 * Mirrors {@link import("./useAssetLocker").SyncState}.
 */
type SyncState = "idle" | "syncing" | "synced" | "error" | "disabled";

type CapturesState = {
  entriesByWorkshop: EntriesByWorkshop;
  /** Seed-card ids the facilitator has removed, per workshop. Device-local
   *  (IDB) — seeds are authored demo content, not synced records, so
   *  hiding them is a presentation choice on the facilitation machine. */
  hiddenSeedsByWorkshop: Record<Workshop["id"], string[]>;
  isHydrated: boolean;
  /** Adapter status — see {@link SyncState}. */
  syncState: SyncState;
  /** ms epoch of the last successful `sync()`; `null` until the first. */
  lastSyncedAt: number | null;
  /** Human-readable error from the most recent failure; cleared on success. */
  syncError: string | null;
  hydrate: () => Promise<void>;
  add: (workshopId: Workshop["id"], capture: UserCapture) => void;
  /**
   * P0.1 — patch-merge an entry's editable fields by id. IDB-only by
   * design: tag/caption edits must NOT round-trip the GitHub-sync path.
   */
  update: (
    workshopId: Workshop["id"],
    id: string,
    patch: CapturePatch,
  ) => void;
  remove: (workshopId: Workshop["id"], id: string) => void;
  /** Facilitator: hide one seed card (device-local, see
   *  {@link CapturesState.hiddenSeedsByWorkshop}). */
  hideSeed: (workshopId: Workshop["id"], seedId: string) => void;
  /** Facilitator: clear the workshop's grid completely — every user
   *  capture is removed (each one sweeps its blob + upstream mirror)
   *  and every authored seed card is hidden. The caller passes the
   *  seed ids since seeds live in authored content, not the store. */
  resetWorkshop: (workshopId: Workshop["id"], seedIds: string[]) => void;
  /**
   * Pull-then-push reconciliation against GitHub for every workshop
   * bucket. No-op when sync is disabled. See implementation comment
   * for the per-step semantics.
   */
  sync: () => Promise<void>;
};

/** Editable fields for {@link CapturesState.update}. Excludes the blob
 *  pointer + GitHub mirror triplet (IDB-only edits). */
export type CapturePatch = Partial<
  Pick<UserCapture, "title" | "caption" | "accent" | "tags">
>;

const IDB_KEY = "xaes-captures";
const HIDDEN_IDB_KEY = "xaes-captures-hidden-seeds";

const WORKSHOP_IDS: Workshop["id"][] = [
  "WS1",
  "WS2",
  "WS3",
  "WS4",
  "WS5",
  "WS6",
  "WS7",
  "WS8",
];

const emptyEntries = (): EntriesByWorkshop => ({
  WS1: [],
  WS2: [],
  WS3: [],
  WS4: [],
  WS5: [],
  WS6: [],
  WS7: [],
  WS8: [],
});

const emptyHiddenSeeds = (): Record<Workshop["id"], string[]> => ({
  WS1: [],
  WS2: [],
  WS3: [],
  WS4: [],
  WS5: [],
  WS6: [],
  WS7: [],
  WS8: [],
});

/** Inflate-side guard for the hidden-seeds map — same threat model as
 *  {@link sanitise}, just a much smaller shape (ws → string ids). */
function sanitiseHiddenSeeds(raw: unknown): Record<Workshop["id"], string[]> {
  const out = emptyHiddenSeeds();
  if (typeof raw !== "object" || raw === null) return out;
  const obj = raw as Record<string, unknown>;
  for (const id of Object.keys(out) as Array<Workshop["id"]>) {
    const list = obj[id];
    if (!Array.isArray(list)) continue;
    out[id] = list.filter((s): s is string => typeof s === "string");
  }
  return out;
}

/**
 * Best-effort sanitiser. See `useAssetLocker.ts` — same threat
 * model. Anything we can't recognise is dropped on the floor; this
 * is the inflate-side guard against a hand-edited IDB blob.
 *
 * Phase 6 — preserves the GitHub mirror fields (`githubPath`,
 * `githubSha`, `githubSyncedAt`) so a reload doesn't orphan the remote
 * capture (we'd otherwise have to re-upload + leak a duplicate).
 */
function sanitise(raw: unknown): EntriesByWorkshop {
  const out = emptyEntries();
  if (typeof raw !== "object" || raw === null) return out;
  const obj = raw as Record<string, unknown>;
  for (const id of Object.keys(out) as Array<Workshop["id"]>) {
    const list = obj[id];
    if (!Array.isArray(list)) continue;
    const cleaned: UserCapture[] = [];
    for (const item of list) {
      if (typeof item !== "object" || item === null) continue;
      const c = item as Record<string, unknown>;
      if (typeof c.id !== "string") continue;
      if (typeof c.title !== "string") continue;
      const kind: CaptureKind =
        c.kind === "whiteboard" ||
        c.kind === "screen" ||
        c.kind === "snapshot" ||
        c.kind === "chat"
          ? c.kind
          : "snapshot";
      const accent: CaptureAccent =
        c.accent === "true-blue" ||
        c.accent === "sky" ||
        c.accent === "gold" ||
        c.accent === "green" ||
        c.accent === "melon" ||
        c.accent === "red"
          ? c.accent
          : "true-blue";
      const source: "Saved" | "Auto-captured" =
        c.source === "Auto-captured" ? "Auto-captured" : "Saved";
      cleaned.push({
        id: c.id,
        workshopId: typeof c.workshopId === "string" ? c.workshopId : id,
        title: c.title,
        activity: typeof c.activity === "string" ? c.activity : "",
        timestamp: typeof c.timestamp === "string" ? c.timestamp : "",
        kind,
        accent,
        source,
        caption: typeof c.caption === "string" ? c.caption : "",
        blobKey: typeof c.blobKey === "string" ? c.blobKey : undefined,
        mimeType: typeof c.mimeType === "string" ? c.mimeType : undefined,
        url: typeof c.url === "string" ? c.url : undefined,
        addedAt: typeof c.addedAt === "number" ? c.addedAt : Date.now(),
        addedBy: typeof c.addedBy === "string" ? c.addedBy : "",
        // P0.1 — free-form tags; preserved when present, absent on legacy
        // entries. Coerced + filtered to strings.
        tags: Array.isArray(c.tags)
          ? c.tags.filter((t): t is string => typeof t === "string")
          : undefined,
        // Phase 6 — GitHub mirror metadata. Strings only; absent on
        // entries that haven't synced yet (IDB-only mode or pre-sync).
        githubPath: typeof c.githubPath === "string" ? c.githubPath : undefined,
        githubSha: typeof c.githubSha === "string" ? c.githubSha : undefined,
        githubSyncedAt:
          typeof c.githubSyncedAt === "string" ? c.githubSyncedAt : undefined,
      });
    }
    out[id] = cleaned;
  }
  return out;
}

async function persist(entries: EntriesByWorkshop): Promise<void> {
  try {
    await idbSet(IDB_KEY, entries);
  } catch {
    /* idb-keyval write failed — best-effort durability, see jsdoc */
  }
}

/**
 * Derive the GitHub filename for a capture. Today every entry is keyed
 * by `UserCapture.id` (uuid-ish) so collisions are nil; the extension
 * comes from `mimeType` via the shared {@link MIME_EXT} map. When the
 * MIME isn't recognised we still upload (the bytes are the point) but
 * with no extension — `mimeFromExt` will then default to
 * `application/octet-stream` on the round-trip back. Mirrors
 * `useAssetLocker.uploadFilename`.
 */
function captureFilename(capture: UserCapture): string {
  const ext = capture.mimeType ? MIME_EXT[capture.mimeType] : "";
  return ext ? `${capture.id}.${ext}` : capture.id;
}

/** Best-effort human title from a GitHub filename. Strips the
 *  extension and replaces dashes/underscores with spaces; falls back to
 *  the raw basename when there's no recognisable shape. Mirrors
 *  `useAssetLocker.labelFromFilename`. */
function titleFromFilename(filename: string): string {
  const base = filename.split(/[\\/]/).pop() ?? filename;
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  return stem.replace(/[-_]+/g, " ").trim() || base;
}

/** True when a filename stem is a machine-minted id — `crypto.randomUUID`
 *  (our `captureFilename` stems) or the `<tool>-<epoch>-<rand>` fallback
 *  mints — rather than a human-chosen name. */
function isMachineStem(stem: string): boolean {
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      stem,
    ) || /^[a-z]+-\d{10,}-[a-z0-9]+$/i.test(stem)
  );
}

/** Title for a pulled capture we've never seen locally. Machine-id stems
 *  (the common case — our own `<uuid>.<ext>` filenames) read as number
 *  soup once de-hyphenated, so they get a dated title instead; genuinely
 *  human-named files keep their name. */
function titleForPulled(
  filename: string,
  stem: string,
  addedAt: number,
): string {
  if (!isMachineStem(stem)) return titleFromFilename(filename);
  const stamp = new Date(addedAt).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `Capture — ${stamp}`;
}

/** Format an ms-epoch as `HH:MM` for the capture card's `timestamp`
 *  field. Centralised so the pull-side reconciliation produces the
 *  same shape as the existing `add()` callers. */
function formatHHMM(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export const useCaptures = create<CapturesState>((set, get) => ({
  entriesByWorkshop: emptyEntries(),
  hiddenSeedsByWorkshop: emptyHiddenSeeds(),
  isHydrated: false,
  syncState: isWorkshopBlobSyncEnabled() ? "idle" : "disabled",
  lastSyncedAt: null,
  syncError: null,
  hydrate: async () => {
    if (get().isHydrated) return;
    try {
      const raw = await idbGet(IDB_KEY);
      const persisted = sanitise(raw);
      const rawHidden = await idbGet(HIDDEN_IDB_KEY);
      set({
        entriesByWorkshop: persisted,
        hiddenSeedsByWorkshop: sanitiseHiddenSeeds(rawHidden),
        isHydrated: true,
      });
    } catch {
      set({ isHydrated: true });
    }
    // IDB-served content is now on screen. Kick off a backend
    // reconciliation in the background so the durable view lands shortly
    // after. Fire-and-forget; errors flow through the store's `syncError`
    // field, not via this hydrate promise.
    if (isWorkshopBlobSyncEnabled()) {
      void get().sync();
    }
  },
  add: (workshopId, capture) => {
    const current = get().entriesByWorkshop;
    const list = current[workshopId] ?? [];
    const next: EntriesByWorkshop = {
      ...current,
      [workshopId]: [...list, capture],
    };
    set({ entriesByWorkshop: next });
    void persist(next);

    // RAG sidecar — title/caption/tags become searchable even when the
    // capture bytes are a non-extractable PNG (or a chat export with no
    // blobKey). BFF-only; fire-and-forget.
    if (isWorkshopBffEnabled()) {
      void upsertSearchIndex(
        workshopId,
        capture.id,
        captureIndexMarkdown({
          workshopId,
          id: capture.id,
          title: capture.title,
          caption: capture.caption,
          kind: capture.kind,
          activity: capture.activity,
          tags: capture.tags,
        }),
      );
    }

    // Phase 6 — mirror the new entry to GitHub when sync is enabled.
    // Only entries that carry a `blobKey` (real bytes) are durable;
    // markdown chat exports that ride on `url` (data-URL) without a
    // blob stay IDB-only because there's nothing the adapter would
    // recognise as a file payload. Fire-and-forget so the UI doesn't
    // block on the round trip; the entry is already in the local
    // store on the synchronous path above.
    if (!capture.blobKey || !isWorkshopBlobSyncEnabled()) return;
    void (async () => {
      try {
        set({ syncState: "syncing", syncError: null });
        const blob = await loadBlob(capture.blobKey!);
        if (!blob) {
          // The blob never landed (saveBlob raced + lost). Skip the
          // upload; the IDB card stays so the user notices and can
          // re-add.
          set({ syncState: "idle" });
          return;
        }
        const filename = captureFilename(capture);
        const asset = await uploadWorkshopBlob(
          "captures",
          workshopId,
          filename,
          blob,
        );
        // Patch the entry with the freshly returned mirror metadata.
        // Read state again — other ops may have mutated the bucket.
        const after = get().entriesByWorkshop;
        const bucket = after[workshopId] ?? [];
        const patched: EntriesByWorkshop = {
          ...after,
          [workshopId]: bucket.map((c) =>
            c.id === capture.id
              ? {
                  ...c,
                  githubPath: asset.path,
                  githubSha: asset.sha,
                  githubSyncedAt: asset.updatedAt,
                }
              : c,
          ),
        };
        set({
          entriesByWorkshop: patched,
          syncState: "synced",
          lastSyncedAt: Date.now(),
          syncError: null,
        });
        void persist(patched);
        // Phase 6 — let sibling tabs know GitHub changed so they can
        // reconcile without waiting for the next polling tick. Mirrors
        // the AssetLocker broadcast path.
        broadcastSync();
      } catch (err) {
        // GithubStorageError carries a tidy `.message`; anything else
        // we surface verbatim. Leave the local IDB entry alone —
        // the user keeps their copy; the status pill flags the miss.
        const message =
          err instanceof GithubStorageError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Upload failed";
        set({ syncState: "error", syncError: message });
      }
    })();
  },
  update: (workshopId, id, patch) => {
    // IDB-only patch-merge (tags/title/caption/accent). Deliberately does
    // NOT touch the GitHub-sync path. Mirrors `useAssetLocker.update`.
    // Caption/title/tag edits still refresh the RAG sidecar when BFF is on.
    const current = get().entriesByWorkshop;
    const list = current[workshopId] ?? [];
    const idx = list.findIndex((c) => c.id === id);
    if (idx < 0) return;
    const updated: UserCapture = { ...list[idx]!, ...patch };
    const nextList = list.slice();
    nextList[idx] = updated;
    const next: EntriesByWorkshop = { ...current, [workshopId]: nextList };
    set({ entriesByWorkshop: next });
    void persist(next);
    if (isWorkshopBffEnabled()) {
      void upsertSearchIndex(
        workshopId,
        updated.id,
        captureIndexMarkdown({
          workshopId,
          id: updated.id,
          title: updated.title,
          caption: updated.caption,
          kind: updated.kind,
          activity: updated.activity,
          tags: updated.tags,
        }),
      );
    }
  },
  remove: (workshopId, id) => {
    const current = get().entriesByWorkshop;
    const list = current[workshopId] ?? [];
    // Capture the doomed entry *before* the filter so we can sweep
    // its blob-store payload (if any). Fire-and-forget: a failed
    // deletion just leaves an orphan blob, which `listBlobKeys`
    // tooling can mop up later. Mirrors `useAssetLocker.remove`.
    const doomed = list.find((c) => c.id === id);
    const next: EntriesByWorkshop = {
      ...current,
      [workshopId]: list.filter((c) => c.id !== id),
    };
    set({ entriesByWorkshop: next });
    void persist(next);
    if (doomed?.blobKey) {
      void deleteBlob(doomed.blobKey);
    }
    void deleteSearchIndex(workshopId, id);
    // Mirror the delete upstream. Needs the path (location); the SHA is a
    // GitHub-only concurrency token (the BFF ignores it), so we no longer
    // require it. Skip when the entry never synced (no path).
    if (isWorkshopBlobSyncEnabled() && doomed?.githubPath) {
      void (async () => {
        try {
          set({ syncState: "syncing", syncError: null });
          await deleteWorkshopBlob(doomed.githubPath!, doomed.githubSha);
          set({
            syncState: "synced",
            lastSyncedAt: Date.now(),
            syncError: null,
          });
          // Phase 6 — see the `add()` broadcast for rationale.
          broadcastSync();
        } catch (err) {
          const message =
            err instanceof GithubStorageError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Delete failed";
          set({ syncState: "error", syncError: message });
        }
      })();
    }
  },
  hideSeed: (workshopId, seedId) => {
    const current = get().hiddenSeedsByWorkshop;
    const list = current[workshopId] ?? [];
    if (list.includes(seedId)) return;
    const next = { ...current, [workshopId]: [...list, seedId] };
    set({ hiddenSeedsByWorkshop: next });
    void idbSet(HIDDEN_IDB_KEY, next).catch(() => {
      /* best-effort durability, same posture as persist() */
    });
  },
  resetWorkshop: (workshopId, seedIds) => {
    // Remove every user capture through the normal path so each one
    // sweeps its blob payload + upstream mirror, then hide every
    // authored seed card too — reset means an empty grid (UAT).
    for (const c of get().entriesByWorkshop[workshopId] ?? []) {
      get().remove(workshopId, c.id);
    }
    const next = {
      ...get().hiddenSeedsByWorkshop,
      [workshopId]: [...new Set(seedIds)],
    };
    set({ hiddenSeedsByWorkshop: next });
    void idbSet(HIDDEN_IDB_KEY, next).catch(() => {
      /* best-effort durability, same posture as persist() */
    });
  },
  sync: async () => {
    // No-op when no blob backend is configured. Keeps the IDB-only dev
    // path free of accidental adapter calls.
    if (!isWorkshopBlobSyncEnabled()) {
      set({ syncState: "disabled" });
      return;
    }
    // Soft-skip when OIDC isn't ready yet — same rationale as
    // `useAssetLocker.sync` (boot hydrate + login-page poll must not
    // paint a sticky "Not signed in" Sync error on the locker footer).
    if (!(await isDocsAuthReady())) {
      set({ syncState: "idle", syncError: null });
      return;
    }
    set({ syncState: "syncing", syncError: null });
    try {
      // Walk every workshop bucket. The reconciliation per bucket is
      // independent — we accumulate the merged state, then commit a
      // single `set` + `persist` at the end so the UI rerenders once.
      const startState = get().entriesByWorkshop;
      const nextState: EntriesByWorkshop = { ...startState };

      for (const workshopId of WORKSHOP_IDS) {
        const remote = await listWorkshopBlobs("captures", workshopId);
        const localBucket = nextState[workshopId] ?? [];

        // GitHub-by-path index for the deleted-upstream check below.
        const remoteByPath = new Map(remote.map((a) => [a.path, a]));
        // IDB-by-path index — only entries that already have a
        // `githubPath` (i.e. previously synced).
        const localByGithubPath = new Map<string, UserCapture>();
        for (const c of localBucket) {
          if (c.githubPath) localByGithubPath.set(c.githubPath, c);
        }

        // ── Step 1 — pull. Anything on GitHub that we don't have
        //    locally gets downloaded, blob-stored, and a fresh
        //    UserCapture entry minted from the filename + mimeType
        //    inference. ──
        const pulled: UserCapture[] = [];
        // Remote assets whose filename stem matches a local capture's id
        // are the same capture coming back around (we mint `<id>.<ext>`
        // filenames) — pulling them would clone the card and stamp it
        // with a uuid-derived title. Instead we adopt the remote
        // metadata onto the local entry, preserving its title/activity,
        // and skip the download entirely.
        const adopted = new Map<string, (typeof remote)[number]>();
        for (const asset of remote) {
          if (localByGithubPath.has(asset.path)) continue;
          // The filename stem is stable across pulls — it usually IS
          // the original capture id; for assets uploaded outside the
          // app it reads human-enough to use as a fallback id.
          const filename = asset.path.split("/").pop() ?? asset.path;
          const stem = filename.replace(/\.[^.]+$/, "");
          const local = localBucket.find(
            (c) => c.id === stem && !c.githubPath,
          );
          if (local) {
            adopted.set(local.id, asset);
            continue;
          }
          // Download the bytes, write the blob to local cache, then
          // wrap it with the inferred MIME so `<Blob.type>` matches
          // what the lightbox expects.
          const rawBlob = await downloadWorkshopBlob(asset.path);
          if (!rawBlob) continue; // 404 race — treat as a soft miss.
          const ext = extFromFilename(filename);
          const mimeType = mimeFromExt(ext);
          const typed = new Blob([rawBlob], { type: mimeType });
          // Blob keys mirror the existing snapshot convention but with
          // a `gh:` prefix so they're easy to spot in dev tooling.
          const blobKey = `gh:${workshopId}:captures:${filename}`;
          await saveBlob(blobKey, typed);
          const remoteUpdated = Date.parse(asset.updatedAt);
          const addedAt = Number.isFinite(remoteUpdated)
            ? remoteUpdated
            : Date.now();
          pulled.push({
            id: stem,
            workshopId,
            title: titleForPulled(filename, stem, addedAt),
            // We can't reliably tell which activity produced a remote
            // capture, so leave the chip blank — the lightbox renders
            // gracefully without it.
            activity: "",
            timestamp: formatHHMM(addedAt),
            // `snapshot` is the best fit when the source tool is
            // unknown; HAH/STR snapshots and chat exports both round
            // to a still image / document in the grid.
            kind: "snapshot",
            accent: "sky",
            source: "Auto-captured",
            caption: "Synced from cloud",
            blobKey,
            mimeType,
            addedAt,
            addedBy: "github-sync",
            githubPath: asset.path,
            githubSha: asset.sha,
            githubSyncedAt: asset.updatedAt,
          });
        }

        // ── Step 2 — push. Anything local with bytes (`blobKey`) but
        //    no `githubPath` gets uploaded; we patch the in-memory
        //    bucket with the returned mirror metadata. ──
        // Link adopted entries before the push pass so they don't
        // re-upload bytes the backend already has.
        const linkedBucket = localBucket.map((c) => {
          const match = adopted.get(c.id);
          return match
            ? {
                ...c,
                githubPath: match.path,
                githubSha: match.sha,
                githubSyncedAt: match.updatedAt,
              }
            : c;
        });
        const pushed = await Promise.all(
          linkedBucket.map(async (c) => {
            if (!c.blobKey || c.githubPath) return c;
            const blob = await loadBlob(c.blobKey);
            if (!blob) return c;
            try {
              const filename = captureFilename(c);
              const asset = await uploadWorkshopBlob(
                "captures",
                workshopId,
                filename,
                blob,
              );
              return {
                ...c,
                githubPath: asset.path,
                githubSha: asset.sha,
                githubSyncedAt: asset.updatedAt,
              };
            } catch (err) {
              // Per-entry failures don't abort the whole sync; the
              // outer try/catch only handles adapter-level fatals.
              // A known adapter error (GitHub 409, BFF 4xx) means leave
              // the local entry untouched and let the next reconciliation
              // tick pick it up.
              if (err instanceof GithubStorageError || err instanceof DocsApiError) {
                return c;
              }
              throw err;
            }
          }),
        );

        // ── Step 3 — upstream deletions. Entries with a `githubPath`
        //    that isn't in the GitHub list mean the capture was
        //    deleted elsewhere. We deliberately do NOT cascade the
        //    delete locally: a follow-up pass can render a "stale"
        //    badge on these cards when the UI is ready. Surfacing the
        //    divergence > silently dropping the user's local copy. ──
        for (const c of pushed) {
          if (c.githubPath && !remoteByPath.has(c.githubPath)) {
            // Marker only — no state change yet. Comment is the spec.
            void c;
          }
        }

        nextState[workshopId] = [...pushed, ...pulled];
      }

      set({
        entriesByWorkshop: nextState,
        syncState: "synced",
        lastSyncedAt: Date.now(),
        syncError: null,
      });
      void persist(nextState);
      // Phase 6 — broadcast after a full reconciliation so a sibling
      // tab kicked off by something else (polling tick, manual pill
      // click) propagates promptly. The 10 s `staleTime` on the
      // polling hook dedupes if multiple tabs race.
      broadcastSync();
    } catch (err) {
      // Known adapter errors (`GithubStorageError` / `DocsApiError`) are
      // folded into the store status — other throws bubble so they show up
      // in console / error boundary instead of being silently swallowed.
      if (err instanceof GithubStorageError || err instanceof DocsApiError) {
        set({ syncState: "error", syncError: err.message });
        return;
      }
      set({ syncState: "error", syncError: "Unexpected sync failure" });
      throw err;
    }
  },
}));
